// ============================================================
// GAME CONSTANTS — 結構性常數與型別定義
// ============================================================
//
// 本檔現在只保留「結構性」常數（不會為了平衡性而調的）：
//   - ELEM enum 與 ECOL 顏色
//   - 棋盤尺寸 (BATTLE_COLS / M3_COLS ...)
//   - 型別定義 (UnitConfig / SpellDef ...)
//
// **所有可調的「平衡性數值」都搬到了 `balance.ts`。**
// 為了向後相容，這裡 re-export balance.ts 的全部值 — 既有
// `import { UCFG, SPELLS, ... } from './constants.ts'` 的程式碼不用改。
//
// 想改 HP / ATK / 法術傷害 / 連鎖加成 / 法術等級閾值 / AI 組成 / 地形 / Shield Wall：
//   → 編輯 `src/balance.ts`
//
// 想加新元素 / 新棋盤大小 / 新法術 type：
//   → 編輯本檔（並同步 balance.ts 加上對應 value）

// ============================================================
// 結構：元素與顏色
// ============================================================

// Session 8: ELEM[5] renamed Dark → Lightning so HUD letter ('L'),
// gem letter ('L'), SCHED slot card label ('Lightning') and spell name
// all stay consistent. SPELLS / ECOL keys updated below to match.
//
// Leader feature (Session 9): ELEM[6/7] = WindFriendly / WindHostile —
// new elements unique to leader BBB. They are NOT in the default gem
// pool; only boards initialised with a leader whose `gemElements`
// includes them will roll these colors. AAA's board stays 1..5.
//
// The two whirlwind spells (SPELLS.WindFriendly / SPELLS.WindHostile)
// use these element ids for their `spellType` field on SpellCommand,
// for HUD level bars, and as the gem color a player needs to chain to
// power them up.
//
// Leader CCC (Session 11): ELEM[8/9] = Grow / Rain — terrain-painting
// spells unique to leader CCC. Grow paints `'grass'` on empty cells AND
// roots enemy units standing on PRE-EXISTING grass (Step B skipped for
// 1 tick). Rain paints `'puddle'` on empty cells AND damages enemy units
// standing on PRE-EXISTING puddles (% of maxSoldiers, scales with power).
// Same sparse-id-set conventions as WindFriendly/Hostile — only boards
// whose leader includes them in `gemElements` will roll these colors.
//
// Leader CCC (Session 12): ELEM[10] = Rockfall — replaces Water/Heal Wave
// in CCC's spell roster. A hostile single-cell (radius=0) spell:
//   - if the targeted cell has any alive unit → the rock "shatters" on
//     impact, dealing massive % maxSoldiers damage to enemy units on
//     that cell. Terrain is left intact (rock broke before settling).
//   - if the targeted cell is empty → a `'boulder'` terrain block is
//     placed there, overwriting whatever terrain was previously on it.
// Boulder cells are impassable (movement.ts auto-detours single-axis)
// and are immune to Grow/Rain transitions. Fire / Lightning damage
// spells break boulders in their radius (back to `'none'`).
export const ELEM = ['None', 'Fire', 'Water', 'Wind', 'Light', 'Lightning', 'WindFriendly', 'WindHostile', 'Grow', 'Rain', 'Rockfall'] as const;

export const ECOL: Record<string, string> = {
  None: '#555',
  Fire: '#ff4444',
  Water: '#4499ff',
  Wind: '#33dd88',
  Light: '#ffcc22',
  Lightning: '#bb55ff',
  // Leader feature — colors chosen for max hue distance from the existing 5:
  //   Fire(0°) / Light(46°) / Wind(147°) / Water(215°) / Lightning(274°).
  // The two biggest gaps are 46°→147° (lime) and 274°→360°/0° (magenta).
  // Earlier draft picked teal (~170°) + rose (~350°), both too close to
  // Water and Fire respectively — these replace with the gap centers:
  //   WindFriendly → lime (~85°)  : visually distinct from Light's yellow
  //                                  AND Wind's green; reads as "gentle air"
  //   WindHostile  → magenta (~315°): visually distinct from Lightning's
  //                                    purple AND Fire's red; reads as "harsh"
  WindFriendly: '#aaee44',
  WindHostile: '#ee44bb',
  // Leader CCC (Session 11) — Grow / Rain occupy the remaining wide hue
  // gaps. Grow sits in the deep olive/forest range (~75°, darker than the
  // existing Wind #33dd88 and lime WindFriendly #aaee44 so all three
  // greens still read distinctly). Rain takes a deep cyan/teal (~190°)
  // that's saturated enough to separate from Water's lighter sky blue
  // (#4499ff) while still reading as "water" rather than "ice".
  Grow: '#669933',
  Rain: '#33aabb',
  // Leader CCC (Session 12) — Rockfall sits in the warm earthy-brown
  // range (~30°, low saturation) so it reads as "stone / rock" rather
  // than as fire (Fire=#ff4444, fully saturated red). Distinct from all
  // five greens / blues / yellows on the existing palette; closest
  // neighbour is Light (#ffcc22) but at half the saturation and much
  // darker so the two never get mistaken on the HUD bar.
  Rockfall: '#9a7a55',
};

// ============================================================
// 結構：棋盤尺寸
// ============================================================

export const BATTLE_COLS = 8;
export const BATTLE_ROWS = 8;
export const M3_COLS = 6;
export const M3_ROWS = 6;

// ============================================================
// 型別定義（值在 balance.ts）
// ============================================================

export interface UnitConfig {
  hp: number;
  atk: number;
  range: number;
  speed: number;
  /** Icon family used by Renderer._drawUnitShape. Session 7 (2026-05-06):
   *  switched from generic primitives (square/diamond/triangle/circle) to
   *  thematic icons (sword/horseshoe/bow/wand) drawn in the same simple
   *  rect+stroke style as the base castle icon. */
  shape: 'sword' | 'horseshoe' | 'bow' | 'wand';
  maxPath: number;
}

export interface BaseConfig {
  team: number;
  tiles: { x: number; y: number }[];
  hp: number;
}

// ============================================================
// Effect model (Session 10 — full effects-list refactor)
// ============================================================
//
// Each spell is now defined as `effects: ReadonlyArray<EffectKind>` —
// the resolver iterates the list and applies each effect independently.
// This replaces the old single-`type` field that allowed only one
// mechanic per spell.
//
// Why: lets a spell layer multiple mechanics from a config table
// (e.g. Whirlwind pulls units AND heals them; Shield Wall sets phys
// AND mag reductions). All values driven by EffectScaling (flat /
// linear / per-power table) so the same shape covers Fire's linear
// `dmg × power` AND Shield Wall's per-level lookup.
//
// Adding a new effect kind:
//   1. Add a new variant to the `EffectKind` discriminated union below.
//   2. Handle it inside `server/spell.ts _applyEffect`.
//   3. (If it produces user-visible numbers) the corresponding field
//      already exists on `SpellImpact` (dmg / heal / buff / defPhys /
//      defMag) — reuse it; otherwise add a new field there.

/**
 * Per-level scaling for an effect's numeric value.
 *   - `flat`   : same value at every powerLevel
 *   - `linear` : value = perLevel × powerLevel (matches the legacy
 *                `dmg × power` style)
 *   - `table`  : value = byPower[powerLevel] (matches Shield Wall's
 *                lookup table; allows arbitrary per-level curves)
 *
 * For `table`, index 0 is the placeholder for power 0 (= "no resource,
 * spell can't be cast"). Reading past the end clamps to the final entry.
 */
export type EffectScaling =
  | { readonly kind: 'flat'; readonly value: number }
  | { readonly kind: 'linear'; readonly perLevel: number }
  | { readonly kind: 'table'; readonly byPower: ReadonlyArray<number> };

/**
 * A single mechanic the spell applies. The resolver iterates a spell's
 * `effects` list and runs each through `_applyEffect`. Optional `scope`
 * overrides the SpellDef.scope for THIS effect only (e.g. a hostile
 * spell that also heals the caster's side could mix scopes).
 *
 * Scope values (Session 12 follow-up):
 *   - 'own'   : only friendly units (same team as caster)
 *   - 'enemy' : only enemy units (opposite team)
 *   - 'all'   : every unit in range, regardless of team (friendly fire).
 *               Used by damage-dealing spells per design — Fire /
 *               Lightning / Rockfall / Rain now hit BOTH teams.
 */
export type EffectKind =
  | {
      /** Direct damage. Subtracts ceil(amount) after tickDefMag reduction. */
      readonly kind: 'damage';
      readonly amount: EffectScaling;
      /**
       * Card Tactician (§8.4 / M3) — 疊加牌單張基礎值。出牌帶 stackCount 時
       * spell.ts 改吃此值 × stackMultiplier(K)，**忽略 `amount`/powerLevel**。
       * 未帶 stackCount（powerLevel 路徑 / 三消遺留 / 測試）則照舊吃 `amount`，
       * 完全不受影響。值見 balance.ts §14 CARD_*_BASE。
       */
      readonly cardBase?: number;
      readonly scope?: 'own' | 'enemy' | 'all';
      /** Multiplier when the target stands on a `'puddle'` cell (Lightning-style). */
      readonly puddleBonus?: number;
      /** If true, also turns `'grass'` cells in the spell radius into `'burningGrass'` (Fire-style). */
      readonly ignite?: boolean;
      /**
       * Session 12 (Leader CCC) — if true, any `'boulder'` terrain cell
       * within `SpellDef.radius` of the cast target is shattered back to
       * `'none'`. Used by Fire (Fireball) and Lightning to give players
       * a counter to enemy Rockfall lockouts. Independent of the per-
       * unit damage application — boulders shatter whether or not a
       * legitimate target stood there.
       */
      readonly breakBoulder?: boolean;
    }
  | {
      /** Heal: healed = ceil(maxSoldiers × ratio), capped at maxSoldiers. */
      readonly kind: 'heal';
      readonly ratio: EffectScaling;
      /** Card Tactician (§8.4 / M3) — 疊加牌單張治療比例基礎值。語意同 damage.cardBase。 */
      readonly cardBase?: number;
      readonly scope?: 'own' | 'enemy' | 'all';
    }
  | {
      /** Flat attack bonus added to `buffAtk`. Negative = debuff.
       *  See `durationTicks` below for lifecycle. */
      readonly kind: 'buffAtk';
      readonly value: EffectScaling;
      /** Card Tactician (§8.4 / M3) — 疊加牌單張 buffAtk 基礎值。語意同 damage.cardBase。 */
      readonly cardBase?: number;
      readonly scope?: 'own' | 'enemy' | 'all';
      readonly durationTicks?: number;
    }
  | {
      /** Physical damage reduction (Shield Wall phys leg).
       *  See `durationTicks` below for lifecycle. */
      readonly kind: 'shieldPhys';
      readonly reduction: EffectScaling;
      /** Card Tactician (§8.4 / M3) — 疊加牌單張物理減免基礎值。spell.ts 疊加
       *  路徑用此值 × stackMultiplier(K) 後夾 CARD_SHIELD_PHYS_CAP。powerLevel
       *  路徑（table）的 power<3=0 行為不受影響。 */
      readonly cardBase?: number;
      readonly scope?: 'own' | 'enemy' | 'all';
      readonly durationTicks?: number;
    }
  | {
      /** Magical damage reduction (Shield Wall mag leg).
       *  See `durationTicks` below for lifecycle. */
      readonly kind: 'shieldMag';
      readonly reduction: EffectScaling;
      /** Card Tactician (§8.4 / M3) — 疊加牌單張法術減免基礎值。夾 CARD_SHIELD_MAG_CAP。 */
      readonly cardBase?: number;
      readonly scope?: 'own' | 'enemy' | 'all';
      readonly durationTicks?: number;
    }
  | {
      /** Whirlwind: pulls units within radius 1 cell toward the spell center.
       *  Uses spell.scope to decide whether allies or enemies are pulled.
       *  Bases are never affected; blocked destinations no-op for that unit.
       *  Whirlwind is instantaneous (no duration concept). */
      readonly kind: 'whirlwindPull';
    }
  | {
      /**
       * Session 11 (Leader CCC) — Grow.
       *
       * Triple mechanic (per target, branched on the cell terrain
       * READ BEFORE the paint pass):
       *   1. **Terrain paint** (always, applied after unit pass): every
       *      `'none'` cell in radius → `'grass'` (permanent).
       *   2. **On pre-existing `'grass'`** → push `'rooted'` modifier
       *      (`durationTicks = rootDurationTicks ?? GROW_ROOT_DURATION_TICKS`).
       *      Pre-existing `'burningGrass'` does NOT root (Fire+Grow combos
       *      don't double-dip).
       *   3. **On pre-existing non-grass** (= `'none'` / `'puddle'` /
       *      `'burningGrass'`) → push `'defDown'` modifier
       *      (`value = _scaleValue(defDownAmount, power)`,
       *       `durationTicks = defDownDurationTicks ?? GROW_DEFDOWN_DURATION_TICKS`).
       *      defDown sums into `BattleUnit.dmgTakenMult` so the unit takes
       *      MORE damage from every source while debuffed. This is what
       *      makes leveling Grow on "open ground" still worthwhile (Session
       *      11 follow-up).
       *
       * (2) and (3) are MUTUALLY EXCLUSIVE per-target (each cell is either
       * grass or not at cast time).
       */
      readonly kind: 'growGrass';
      readonly scope?: 'own' | 'enemy' | 'all';
      readonly rootDurationTicks?: number;
      /** Session 11 follow-up — defense-down (vulnerability) amount applied
       *  to enemies standing on non-grass cells. Sum-aggregated across
       *  modifiers into `BattleUnit.dmgTakenMult`. Optional for back-compat
       *  with older fixtures (omitted → no defDown, single-effect spell). */
      readonly defDownAmount?: EffectScaling;
      /** Session 11 follow-up — duration of the defDown modifier in ticks.
       *  Defaults to GROW_DEFDOWN_DURATION_TICKS (= 2 → covers 1 combat
       *  tick post-cast, same as WindHostile's debuff). */
      readonly defDownDurationTicks?: number;
    }
  | {
      /**
       * Session 11 (Leader CCC) — Rain.
       *
       * Triple mechanic (per target, branched on the cell terrain
       * READ BEFORE the paint pass):
       *   1. **Terrain paint** (always, applied after unit pass): every
       *      `'none'` cell in radius → `'puddle'` (permanent).
       *   2. **On pre-existing `'puddle'`** → deal damage
       *      `ceil(maxSoldiers × _scaleValue(drownRatio, power))`. Magical
       *      damage path (reduced by `tickDefMag`).
       *   3. **On pre-existing non-puddle** (= `'none'` / `'grass'` /
       *      `'burningGrass'`) → deal smaller "drizzle" damage
       *      `ceil(maxSoldiers × _scaleValue(directDmgRatio, power))`.
       *      Same magical damage path. The point is to make Rain's level
       *      meaningful even when there's no puddle (Session 11 follow-up).
       *
       * (2) and (3) are MUTUALLY EXCLUSIVE per-target. `directDmgRatio` is
       * optional for back-compat with older fixtures.
       */
      readonly kind: 'rainPuddle';
      readonly scope?: 'own' | 'enemy' | 'all';
      readonly drownRatio: EffectScaling;
      /** Session 11 follow-up — small base damage ratio applied to enemies
       *  standing on non-puddle cells. Capped low by design (see
       *  RAIN_DIRECT_DMG_BY_POWER in balance.ts §8). Optional. */
      readonly directDmgRatio?: EffectScaling;
    }
  | {
      /**
       * Session 12 (Leader CCC) — Rockfall.
       *
       * Single-cell hostile spell (radius=0 — only the targeted tile is
       * affected). Dual mechanic, branched on whether ANY alive unit
       * (friend or foe) stands on the targeted cell:
       *   1. **Cell occupied** → the falling rock shatters on impact and
       *      deals `ceil(maxSoldiers × _scaleValue(dmgRatio, power))`
       *      magical-class damage to every enemy-team unit on that cell.
       *      Friendly units are not damaged (scope filter). Terrain is
       *      left intact (the rock broke before it could settle).
       *   2. **Cell empty** → a `'boulder'` terrain block is dropped on
       *      the cell, overwriting whatever terrain was there. Boulder
       *      cells are impassable; movement.ts auto-detours around them.
       *      Fire / Lightning damage spells with `breakBoulder: true`
       *      can shatter the boulder back to `'none'`.
       *
       * The "any unit" check uses friendly units too so a Rockfall can't
       * accidentally bury a teammate under stone — design parity with
       * Grow/Rain's "PRE-paint terrain" branch logic. The damage half
       * still respects scope so a misclick onto your own unit just whiffs.
       */
      readonly kind: 'rockfall';
      readonly scope?: 'own' | 'enemy' | 'all';
      /** Per-power table for the % maxSoldiers shatter damage when the
       *  cell is occupied. Mirrors `rainPuddle.drownRatio` shape. */
      readonly dmgRatio: EffectScaling;
    };

// ============================================================
// Unit modifiers (Session 10 follow-up)
// ============================================================
//
// `BattleUnit.buffAtk` / `.tickDefPhys` / `.tickDefMag` are derived
// cached scalars; the SOURCE OF TRUTH for active effects is the
// per-unit `modifiers: UnitModifier[]` list.
//
// `_applyEffect` pushes a UnitModifier each time a buffAtk / shieldPhys /
// shieldMag effect fires; `tick.ts` end-of-tick decrements every
// modifier's `ticksRemaining` and removes expired ones; both paths call
// `_recomputeScalars(u)` so the cached scalars combat.ts / spell.ts
// read stay accurate (sum for buffAtk, max for shields).
//
// Duration semantics — `durationTicks` on the EFFECT side maps directly
// to the modifier's initial `ticksRemaining`:
//
//   durationTicks: N  →  modifier survives N end-of-tick decrements.
//
// Because Step A spells (Bless, Shield Wall) cast BEFORE same-tick
// combat and Step D spells (Whirlwind hostile debuff) cast AFTER
// same-tick combat, the EFFECTIVE combat-tick coverage differs:
//
//   Phase = 'friendly' (Step A) → combat covered = N    (cast tick included)
//   Phase = 'hostile'  (Step D) → combat covered = N-1  (cast tick already past)
//
// Same-source (kind + source ELEM id) modifiers DON'T STACK — a second
// cast REPLACES the existing modifier's value + ticksRemaining. So
// double-casting Whirlwind hostile on the same unit refreshes the
// debuff rather than compounding it. Different sources (e.g. Bless +
// Whirlwind hostile = both buffAtk kind but different ELEM source) still
// coexist and sum/max as usual.

export interface UnitModifier {
  /**
   * Which scalar this modifier feeds into.
   *
   * Session 11 — `'rooted'` is a boolean-style modifier (value is unused,
   * presence alone matters) that maps to the cached `BattleUnit.rooted`
   * scalar. `movement.ts` checks this flag in `_collectIntents` and
   * skips the unit's Step B entirely (both sub-phases). Created by the
   * Grow (`growGrass`) effect when cast on a pre-existing grass cell.
   *
   * Session 11 follow-up — `'defDown'` is a vulnerability modifier (value
   * = the % more damage taken, e.g. 0.05 = +5%). SUM-aggregated across
   * modifiers into `BattleUnit.dmgTakenMult` (= 1 + sum). Created by Grow
   * on non-grass cells; reduces effective defense by amplifying incoming
   * damage at the `applyDamageToUnit` callsite (so it stacks with shield
   * reductions and vulnerable-targets pattern is consistent across every
   * damage path: combat / spell / DOT).
   */
  readonly kind: 'buffAtk' | 'shieldPhys' | 'shieldMag' | 'rooted' | 'defDown';
  /** Per-modifier contribution (signed for buffAtk; 0..1 for shields; ignored for rooted; positive 0..1 for defDown = fraction of EXTRA damage taken). */
  value: number;
  /** Decremented at end of each tick; modifier removed when <= 0. */
  ticksRemaining: number;
  /** Original `durationTicks` at apply / refresh time. Not read by any
   *  gameplay logic; the client renderer uses it to derive a fade ratio
   *  (`ticksRemaining / startingTicks`) for the modifier badge so the
   *  player can see "this debuff is about to expire". */
  startingTicks: number;
  /** ELEM id of the spell that created this modifier. Used as same-source
   *  identity key for replace-vs-stack decisions, plus diagnostics. */
  readonly source: number;
}

/**
 * Spell definition (Session 10 refactor — pure effects model).
 *
 * Resolution flow:
 *   1. `phase` decides Step A (friendly) vs Step D (hostile).
 *   2. `scope` is the default target filter (own / enemy team).
 *   3. `effects` is the ordered list of mechanics; resolver runs each
 *      against units within `radius` (Manhattan) of the cast target.
 *
 * All spell-wide knobs (radius/phase/scope) are shared by every effect;
 * an individual effect can override `scope` to fan out (e.g. hostile
 * spell that also heals caster's side).
 */
export interface SpellDef {
  /** Display name shown in the SCHED UI. */
  readonly name: string;
  /** Manhattan radius around the cast tile. */
  readonly radius: number;
  /** EXEC sub-step routing: 'friendly' = Step A, 'hostile' = Step D. */
  readonly phase: 'friendly' | 'hostile';
  /** Default target-team filter for effects without their own `scope`.
   *  'all' = friendly fire (Session 12 follow-up): damage spells now
   *  affect both teams. See EffectKind doc block for full semantics. */
  readonly scope: 'own' | 'enemy' | 'all';
  /** Ordered list of mechanics this spell applies. */
  readonly effects: ReadonlyArray<EffectKind>;
}

// ============================================================
// 平衡性數值（re-export 自 balance.ts）
// ============================================================
//
// 這些 re-export 是為了向後相容 — 舊 `import { UCFG, SPELLS, ... } from './constants.ts'`
// 不用改動。**新加 import 建議直接從 './balance.ts' 取**，意圖比較清楚。

export {
  // §1-3 兵 / 法 / 基
  UCFG,
  SPELLS,
  BASES,
  // §5 Phase 計時
  SCHED_TIMER_SECS,
  // §10 佈陣規則 (DeploymentRules)
  MANUAL_RULES,
  PRESET_RULES,
  DEFAULT_MANUAL_RULES,
  AI_MAX_PLACEMENT_ATTEMPTS,
  AI_MIN_PATH_STEPS,
  AI_ROW_RANGE,
  // §14 卡牌系統常數 (Card Tactician)
  CARD_HAND_CAP,
  CARD_OPENING_DRAW,
  CARD_DRAW_PER_TURN,
  CARD_STACK_DAMP,
  CARD_STACK_DAMP_TAIL,
  CARD_EFFECT_FIXED_POWER,
  CARD_FIRE_BASE,
  CARD_LIGHTNING_BASE,
  CARD_HEAL_BASE,
  CARD_BUFF_BASE,
  CARD_SHIELD_PHYS_SINGLE,
  CARD_SHIELD_MAG_SINGLE,
  CARD_SHIELD_PHYS_CAP,
  CARD_SHIELD_MAG_CAP,
  stackMultiplier,
} from './balance.ts';
